import React, {createContext, useContext, useEffect, useState} from 'react'
import _ from 'lodash'
import {useInterval} from 'react-use'
import {Icon} from 'semantic-ui-react'
import './style.css'
import {captureException} from '@sentry/react'

export const MessageContext = createContext<IMessageContext>({
  messages: [],
  closeMessage: () => {},
  logError: () => {},
  logInfo: () => {},
})

export interface IMessage {
  type: 'info' | 'error'
  message: string
  token: string
  expires: number
}

export interface IMessageContext {
  messages: IMessage[]
  closeMessage: (message: string) => void
  logError: (message: string, ...extra: any[]) => void
  logInfo: (message: string, ...extra: any[]) => void
}

export interface INotificationsWrapProps {
  children: React.ReactNode
}

export interface INotificationMessageProps {
  message: IMessage
  onClick: () => void
}

export function NotificationsWrap(props: INotificationsWrapProps) {
  const [messages, setMessages] = useState<IMessage[]>([])
  const [tokens, setTokens] = useState<string[]>([])

  useEffect(() => {
    setTokens(
      _.chain(messages)
        .map((m) => _tokenizeMessage(m.message))
        .uniq()
        .value()
    )
  }, [messages])

  useInterval(() => {
    if (messages.length === 0) return
    setMessages((prev) =>
      _.chain(prev)
        .filter((m) => m.expires > Date.now())
        .value()
    )
  }, 1000)

  function _tokenizeMessage(message: string): string {
    return _.chain(message).toLower().trim().value()
  }

  function closeMessage(message: string) {
    const t = _tokenizeMessage(message)
    setMessages((prev) =>
      _.chain(prev)
        .filter((m) => m.token !== t)
        .value()
    )
  }

  function logError(message: string, ...extra: any[]) {
    // throw new Error(message);
    console.error(..._.compact(['ERROR', message, ...extra]))
    captureException(new Error(message), {extra: {...extra}})
    const ttl = 10 * 1000 // ms
    const _token = _tokenizeMessage(message)
    if (_.includes(tokens, _token)) return
    const msg: IMessage = {
      type: 'error',
      message,
      token: _token,
      expires: Date.now() + ttl,
    }
    setMessages((prev) => [...prev, msg])
  }

  function logInfo(message: string, ...extra: any[]) {
    console.info(..._.compact(['INFO', message, ...extra]))
    const ttl = 5 * 1000 // ms
    const _token = _tokenizeMessage(message)
    if (_.includes(tokens, _token)) return
    const msg: IMessage = {
      type: 'info',
      message,
      token: _token,
      expires: Date.now() + ttl,
    }
    setMessages((prev) => [...prev, msg])
  }

  return (
    <MessageContext.Provider
      value={{
        messages,
        closeMessage,
        logError,
        logInfo,
      }}
      {...props}
    />
  )
}

function NotificationMessage(props: INotificationMessageProps) {
  const {message: m, ...rest} = props
  return (
    <div
      key={m.message}
      className={`notification ${m.type}`}
      {...rest}
    >
      <Icon name={m.type === 'error' ? 'cancel' : 'info'} /> {m.message}
    </div>
  )
}

export function NotificationWidget() {
  const {messages, closeMessage} = useContext(MessageContext)

  if (!(messages && messages.length > 0)) return null

  return (
    <div className='notification-widget'>
      {messages.map((m, i) => {
        return (
          <NotificationMessage
            key={i}
            message={m}
            onClick={() => closeMessage(m.message)}
          />
        )
      })}
    </div>
  )
}
